import { set, defaults, omit } from 'lodash-es';
import { signOut } from 'firebase/auth';
import { auth } from '@/services/firebase';

import {
  currencySymbol,
  ISOCode,
} from '@/utils/currencies';

import {
  loginUser,
  getCurrentUser,
} from '@/api/auth';
import {
  updateUserProfile,
  deleteUserData,
} from '@/api/profile';
import restorePassword from '@/store/modules/user/restore-password';
import signUp from '@/store/modules/user/signUp';
import stripe from '@/store/modules/user/stripe';
import appCustomer from '@/store/modules/user/appCustomer';
import subscriptionPlans from '@/store/modules/user/subscription-plans';
import { Role } from '@/utils/auth';
import { SETUP_PROGRESS } from '@/constants/setupProgresses';
import { TIER } from '@/constants/price';

const DEFAULT_SETUP_ITEMS = Object.fromEntries(Object.values(SETUP_PROGRESS).map(value => ([value, false])));

const TIER_GIFT_CAMPAIGN_FOR_NON_AUTH_USERS = {
  id: 'free_pro14',
  durationMs: 1.21e+9, // 14 days
  tierKey: TIER.PRO,
};

export default {
  namespaced: true,
  modules: {
    restorePassword,
    signUp,
    stripe,
    appCustomer,
    subscriptionPlans,
  },
  state: {
    user: null,
    token: null,
    validationErrorEdit: null,
    editProfileLoading: false,
    deleteAccountLoading: false,
    loginLoading: false,
  },
  mutations: {
    setToken(state, token) {
      localStorage.setItem('authToken', token);
      state.token = token;
    },
    setUser(state, payload) {
      state.user = Object.assign({}, state.user, payload);
      localStorage.setItem('user', JSON.stringify({ ...payload }));
    },
    setEditError(state, message) {
      state.validationErrorEdit = message;
    },
    setFacebookData(state, data) {
      Object.assign(state.user, data);
    },
    setEditProfileLoading(state, isLoading) {
      state.editProfileLoading = isLoading;
    },
    setDeleteAccountLoading(state, isLoading) {
      state.deleteAccountLoading = isLoading;
    },
    setLoginLoading(state, isLoading) {
      state.loginLoading = isLoading;
    },
    setIsProfileLogoHidden(state, visibility) {
      set(state.user, 'options.isProfileLogoHidden', visibility);
    },
    resetUser(state) {
      state.user = null;
    },
  },
  actions: {
    async editProfile({ commit, dispatch }, payload) {
      try {
        commit('setEditProfileLoading', true);
        const data = await dispatch('updateUser', payload);
        commit('setUser', data);
        commit('setEditError', null);
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      } finally {
        commit('setEditProfileLoading', false);
      }
    },
    async updateUser(context, payload) {
      return new Promise((resolve, reject) => {
        updateUserProfile(payload)
          .then((response) => {
            const { error } = response.data;
            if (error) {
              reject(error);
            }
            resolve(response.data);
          })
          .catch(reject);
      });
    },
    async login({ commit, dispatch }, { email, password }) {
      try {
        commit('setLoginLoading', true);
        const { data } = await loginUser({ email, password });
        commit('setUser', data.user);
        commit('setToken', data.token);
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      } finally {
        commit('setLoginLoading', false);
      }
    },
    async logout({ dispatch }) {
      try {
        await signOut(auth);
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      }
    },
    async loadUser({ dispatch }) {
      try {
        const { data } = await getCurrentUser();
        dispatch('setUser', data);
      } catch (e) {
        console.log(e);
        // dispatch('errorOverlay/setMessage', e, { root: true });
      }
    },
    async setUser({ commit, dispatch, getters }, user) {
      try {
        commit('setUser', user);
        await dispatch('user/stripe/getAccount', getters.stripeAccount, { root: true });
      } catch (e) {
        console.log(e);
      }
    },
    async deleteAccount({ commit, dispatch }) {
      try {
        commit('setDeleteAccountLoading', true);
        await deleteUserData();
        commit('setUser', {});
        await dispatch('auth/signOut', null, { root: true });
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
      } finally {
        commit('setDeleteAccountLoading', false);
      }
    },
    async updateSetupProgress({ getters, dispatch }, payload) {
      const setupProgress = getters.setupProgress;
      const needUpdate = Object.keys(payload).some(key => !(key in setupProgress) || payload[key] !== setupProgress[key]);
      if (needUpdate) {
        dispatch('editProfile', { setupProgress: { ...setupProgress, ...payload } });
      }
    },
  },
  getters: {
    user: state => state.user || {},
    userTierKey: (state, getters) => getters.user?.tier?.key || TIER.FREE,
    // TODO: add FREE features by default
    userFeatures: (state, getters) => getters.user.tier?.features || {},
    userLoggedIn: state => !!Object.keys(state.user || {}).length && state.user,
    availableTierGiftCampaign: (state, getters) => {
      if (state.user) {
        return getters.user.availableTierGiftCampaign;
      }

      return TIER_GIFT_CAMPAIGN_FOR_NON_AUTH_USERS;
    },
    isFreePro14CampaignAvailable: (state, getters) => getters.availableTierGiftCampaign?.id === 'free_pro14',
    isUserTierGiftEnabled: (state, getters) => !!getters.user.tierGiftId,
    editProfileLoading: state => state.editProfileLoading,
    stripeAccount: (state, getters) => getters.user.stripe && state.user.stripe.account,
    deleteAccountLoading: state => state.deleteAccountLoading,
    currency: (state, getters) => getters.user.stripe?.currency || ISOCode.POUND_STERLING,
    currencySymbol: (state, getters) => currencySymbol[getters.currency],
    // exclude legacy join (join discord) field
    setupProgress: (state, getters) => omit(defaults(getters.user.setupProgress, DEFAULT_SETUP_ITEMS), 'join'),
    isUserSupporter: state => state.user?.role === Role.SUPPORTER,
    isProfileLogoHidden: (state, getters) => !!getters.user.options?.isProfileLogoHidden,
    customerId: (state, getters) => getters.user.stripe?.customerId,
  },
};
